import { Check } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Modal } from '@/shared/components/Modal'
import { COUNTRY_CODES, countryFlag } from '@/shared/lib/country'


interface CountryPickerModalProps {
  open: boolean
  selected?: string | null
  onSelect: (code: string) => void
  onClose: () => void
}

/** Searchable country list, like the iOS settings country picker. */
export function CountryPickerModal({ open, selected, onSelect, onClose }: CountryPickerModalProps) {
  const { t, i18n } = useTranslation('settings')
  const [search, setSearch] = useState('')

  const countries = useMemo(() => {
    const names = new Intl.DisplayNames([i18n.language], { type: 'region' })
    return COUNTRY_CODES.map((code) => ({ code, name: names.of(code) ?? code })).sort((a, b) =>
      a.name.localeCompare(b.name, i18n.language),
    )
  }, [i18n.language])

  const query = search.trim().toLowerCase()
  const filtered = query
    ? countries.filter(
        (country) =>
          country.name.toLowerCase().includes(query) || country.code.toLowerCase() === query,
      )
    : countries

  return (
    <Modal open={open} onClose={onClose} title={t('countryPickerTitle')} size="sm">
      <input
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('countrySearch')}
        className="border-border bg-canvas text-fg focus:border-primary w-full rounded-xl border px-3.5 py-2.5 text-sm focus:outline-none"
      />
      <div className="mt-3 max-h-80 space-y-0.5 overflow-y-auto">
        {filtered.map((country) => (
          <button
            key={country.code}
            type="button"
            onClick={() => onSelect(country.code)}
            className="hover:bg-surface-hover flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition"
          >
            <span className="text-lg">{countryFlag(country.code)}</span>
            <span className="text-fg flex-1 truncate text-sm">{country.name}</span>
            {selected?.toUpperCase() === country.code && (
              <Check className="text-primary size-4 shrink-0" />
            )}
          </button>
        ))}
      </div>
    </Modal>
  )
}
